import React, { useState, useEffect } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import { downloadCSV } from '../ui/csv';
import { apiCall, API_BASE_URL } from '../config';
import { getStationName } from '../utils/stationMapping';
import { normalizeStationName } from '../utils/stationNormalization';
import TimesheetReview from './TimesheetReview';
// Debug-Ausgaben stumm (Philipp, 25.09.2026: Schichtplanung lud bei offener Konsole spürbar langsam – pro Neuzeichnen
// wurden hunderte Zeilen samt kompletter Datenlisten geloggt). Fehler/Warnungen bleiben. Zum Debuggen:
// localStorage.setItem('debugLogs', '1') + neu laden.
// eslint-disable-next-line no-shadow, no-redeclare
const console = (() => { let on = false; try { on = window.localStorage.getItem('debugLogs') === '1'; } catch (_) { on = false; } return on ? window.console : { ...window.console, log: () => {}, debug: () => {}, info: () => {} }; })();


// Globale Hilfsfunktion für sichere Stundenberechnung mit Mitternachts-Unterstützung
const calculateSafeHours = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  try {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    // Mitternachts-überschreitende Schichten: Wenn Endzeit kleiner als Startzeit
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60; // Füge 24 Stunden hinzu
    }
    
    const diffMinutes = endMinutes - startMinutes;
    const hours = diffMinutes / 60;
    
    // Frontend-Sicherheitscheck
    if (hours < 0) {
      console.error('🚨 Frontend Safety Check: Negative hours detected!', {
        startTime, endTime, hours, 
        message: 'This should not happen with proper midnight logic'
      });
      return 0; // Sicherer Fallback
    }
    
    if (hours > 24) {
      console.warn('⚠️ Frontend Warning: Shift longer than 24 hours detected', {
        startTime, endTime, hours
      });
    }
    
    return parseFloat(hours.toFixed(2));
  } catch (error) {
    console.error('❌ Error in calculateSafeHours:', error, { startTime, endTime });
    return 0;
  }
};

// Intelligente Backend-Stunden-Korrektur für bereits berechnete Werte
const correctBackendHours = (backendHours, context = '') => {
  if (typeof backendHours !== 'number') {
    const parsed = parseFloat(backendHours);
    if (isNaN(parsed)) return 0;
    backendHours = parsed;
  }
  
  // Intelligente Korrektur für negative Stunden (Backend-Mitternachts-Problem)
  if (backendHours < 0) {
    // Für Mitternachts-überschreitende Schichten: 24 + negative Stunden
    const correctedHours = 24 + backendHours;
    
    if (correctedHours > 0 && correctedHours <= 23) {
      console.info(`🔄 ActualHoursTracking: Correcting midnight shift:`, {
        originalValue: backendHours,
        correctedValue: correctedHours,
        context
      });
      return correctedHours;
    }
  }
  
  return Math.max(0, backendHours); // Mindestens 0
};

const ActualHoursTracking = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    return new Date().getFullYear();
  });
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);
  const [rowImage, setRowImage] = useState(null); // Zeilen-Ausschnitt vom Stundenzettel { id, name, time }

  // Unbezahlte Pause einfügen (Philipp, 24.09.2026): steht selten, handschriftlich auf dem Zettel. Der Server teilt den
  // Eintrag an der Pause – alle Summen/Abrechnungen stimmen dadurch automatisch.
  const sortSegs = (list) => [...list].sort((a, b) => { const m = (t) => { const [h, mm] = String(t).split(':').map(Number); return (h < 6 ? h + 24 : h) * 60 + mm; }; return m(a.actual_start_time) - m(b.actual_start_time); });
  const addBreak = async (entries) => {
    const segs = sortSegs(entries);
    const val = window.prompt(`Unbezahlte Pause in Minuten (wird von ${segs[0].actual_start_time}–${segs[segs.length - 1].actual_end_time} abgezogen):`, '30');
    if (!val) return;
    const minutes = parseInt(String(val).replace(/[^0-9]/g, ''), 10);
    if (!minutes) { notify('Bitte nur die Minuten eingeben, z. B. 30'); return; }
    // im längsten Teil einfügen (bei mehreren Pausen)
    const len = (e) => { const t = (x) => { const [h, mm] = String(x).split(':').map(Number); return h * 60 + mm; }; let d = t(e.actual_end_time) - t(e.actual_start_time); if (d <= 0) d += 1440; return d; };
    const target = [...segs].sort((a, b) => len(b) - len(a))[0];
    try {
      await apiCall(`/actual-hours/${target.id}/break`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ minutes }) });
      fetchActualHours();
    } catch (err) { notify('Pause konnte nicht eingetragen werden: ' + err.message); }
  };
  const removeBreak = async (entries) => {
    const last = sortSegs(entries).filter((e) => e.break_of).pop(); if (!last) return;
    if (!window.confirm('Pause entfernen und die Zeiten wieder zusammenführen?')) return;
    try { await apiCall(`/actual-hours/${last.id}/break`, { method: 'DELETE' }); fetchActualHours(); } catch (err) { notify('Fehlgeschlagen: ' + err.message); }
  };
  const [plannedShifts, setPlannedShifts] = useState([]);
  const [actualHours, setActualHours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [viewMode, setViewMode] = useState('station'); // 'station' oder 'employee'

  // CSV-Export der erfassten Ist-Stunden (für Lohn/Audit)
  const handleExportCSV = () => {
    if (!actualHours || actualHours.length === 0) {
      notify('Keine Ist-Stunden zum Exportieren vorhanden.');
      return;
    }
    const rows = actualHours.map((e) => {
      const emp = employees.find((u) => u.id === e.user_id);
      return {
        Mitarbeiter: emp ? `${emp.firstname} ${emp.lastname}` : (e.user_id ?? ''),
        Datum: e.date || e.work_date || '',
        Station: getStationName(e.station_id || e.station || ''),
        Start: e.actual_start_time || '',
        Ende: e.actual_end_time || '',
        Stunden: calculateSafeHours(e.actual_start_time, e.actual_end_time),
      };
    });
    const ok = downloadCSV(`ist-stunden-${selectedYear}.csv`, rows, [
      { key: 'Mitarbeiter', label: 'Mitarbeiter' },
      { key: 'Datum', label: 'Datum' },
      { key: 'Station', label: 'Station' },
      { key: 'Start', label: 'Start' },
      { key: 'Ende', label: 'Ende' },
      { key: 'Stunden', label: 'Ist-Stunden' },
    ]);
    if (ok) notify(`CSV mit ${rows.length} Einträgen erstellt.`);
  };

  useEffect(() => {
    if (viewMode === 'station' && selectedDate) {
      fetchPlannedShifts();
      fetchActualHours();
    } else if (viewMode === 'employee' && selectedYear && selectedEmployee) {
      fetchEmployeeYearData();
    }
  }, [selectedDate, selectedYear, selectedEmployee, viewMode]);

  useEffect(() => {
    // Lade Mitarbeiterliste beim Komponenten-Mount
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const data = await apiCall('/users');
      setEmployees(data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const fetchPlannedShifts = async () => {
    setLoading(true);
    try {
      const data = await apiCall(`/planning/assignments/${selectedDate}`);
      console.log('🔍 ActualHoursTracking: Received planned shifts:', data);
      console.log('🔍 ActualHoursTracking: First shift data:', data[0]);
      setPlannedShifts(data);
    } catch (error) {
      console.error('Fehler beim Laden der Schichtplanung:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActualHours = async () => {
    try {
      console.log('🔄 Fetching actual hours for date:', selectedDate);
      const data = await apiCall(`/actual-hours/${selectedDate}`);
      console.log('📈 Received actual hours data:', data.length, 'entries', data);
      
      // Frontend-Analyse: Prüfe auf potentielle Backend-Probleme
      data.forEach((entry, index) => {
        if (entry.actual_start_time && entry.actual_end_time) {
          const frontendHours = calculateSafeHours(entry.actual_start_time, entry.actual_end_time);
          
          // Prüfe auf Mitternachts-Schichten
          const isOvernightShift = entry.actual_end_time < entry.actual_start_time;
          if (isOvernightShift) {
            console.info(`🌙 Midnight shift detected for entry ${index}:`, {
              start: entry.actual_start_time,
              end: entry.actual_end_time,
              frontendCalculation: frontendHours,
              userInfo: entry.user_name
            });
          }
          
          if (frontendHours <= 0) {
            console.warn(`⚠️ Problematic entry ${index} detected:`, {
              entry,
              frontendCalculation: frontendHours,
              isOvernightShift
            });
          }
        }
      });
      
      setActualHours(data);
    } catch (error) {
      console.error('Fehler beim Laden der Ist-Stunden:', error);
    }
  };

  const fetchEmployeeYearData = async () => {
    setLoading(true);
    try {
      console.log(`🔄 Fetching employee data for year ${selectedYear}, employee ${selectedEmployee}`);
      
      // Lade alle geplanten Schichten für das Jahr und den Mitarbeiter
      const shiftsData = await apiCall(`/planning/assignments/year/${selectedYear}/employee/${selectedEmployee}`);
      console.log('📅 Received planned shifts for year:', shiftsData.length, 'entries');
      setPlannedShifts(shiftsData);
      
      // Lade alle Ist-Stunden für das Jahr und den Mitarbeiter
      const hoursData = await apiCall(`/actual-hours/year/${selectedYear}/employee/${selectedEmployee}`);
      console.log('📈 Received actual hours for year:', hoursData.length, 'entries');
      setActualHours(hoursData);
      
    } catch (error) {
      console.error('Fehler beim Laden der Jahres-Daten:', error);
      // Fallback: leere Arrays setzen
      setPlannedShifts([]);
      setActualHours([]);
    } finally {
      setLoading(false);
    }
  };

  const saveActualHours = async (entry) => {
    try {
      const method = entry.id ? 'PUT' : 'POST';
      const endpoint = entry.id 
        ? `/actual-hours/${entry.id}` 
        : '/actual-hours';
      
      await apiCall(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });

      fetchActualHours();
      setEditingEntry(null);
      setShowAddEntry(false);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      notify('Fehler beim Speichern: ' + error.message);
    }
  };

  const deleteActualHours = async (id) => {
    if (await confirmDialog('Eintrag wirklich löschen?')) {
      try {
        await apiCall(`/actual-hours/${id}`, {
          method: 'DELETE',
        });
        fetchActualHours();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
        notify('Fehler beim Löschen: ' + error.message);
      }
    }
  };

  const createFromPlanned = async (plannedShift, customStartTime = null, customEndTime = null) => {
    console.log('🚀 Starting createFromPlanned for shift:', plannedShift);
    console.log('🚀 Custom times provided:', { customStartTime, customEndTime });
    
    const userId = plannedShift.assignedUsers?.[0]?.id || plannedShift.user_id;
    const userName = plannedShift.assignedUsers?.[0]?.name || 
                     plannedShift.user_name || 
                     (plannedShift.firstname && plannedShift.lastname ? `${plannedShift.firstname} ${plannedShift.lastname}` : '') ||
                     'Unbekannt';
    
    // Prüfe ob bereits ein Eintrag für diese Schicht existiert
    const existingEntries = getActualHoursForShift(plannedShift);
    const existingEntry = existingEntries.length > 0 ? existingEntries[0] : null;
    
    console.log('📊 Current actual hours before save:', actualHours.length, 'entries');
    console.log('📊 Existing entry found:', existingEntry ? `ID ${existingEntry.id}` : 'NONE');
                     
    const entry = {
      user_id: userId,
      user_name: userName,
      date: selectedDate,
      station_id: plannedShift.station_id,
      station_name: plannedShift.station || `Station ${plannedShift.station_id}`,
      planned_start_time: plannedShift.start_time,
      planned_end_time: plannedShift.end_time,
      actual_start_time: customStartTime || plannedShift.start_time,
      actual_end_time: customEndTime || plannedShift.end_time,
      notes: ''
    };
    
    // Wenn bereits ein Eintrag existiert, verwende seine ID für UPDATE
    if (existingEntry) {
      entry.id = existingEntry.id;
      console.log('� Will UPDATE existing entry with ID:', entry.id);
    } else {
      console.log('➕ Will CREATE new entry');
    }
    
    console.log('� Final entry to save:', entry);

    // Direkt speichern ohne Modal zu öffnen
    try {
      await saveActualHours(entry);
      console.log('✅ Save completed, actual hours will be refreshed');
    } catch (error) {
      console.error('Fehler beim Übernehmen der geplanten Stunden:', error);
      notify('Fehler beim Übernehmen: ' + error.message);
    }
  };

  const getActualHoursForShift = (plannedShift) => {
    const userId = plannedShift.assignedUsers?.[0]?.id || plannedShift.user_id;
    const plannedStationId = plannedShift.station_id;
    const plannedStartTime = plannedShift.start_time;
    
    console.log('🔍 Looking for actual hours for shift:', {
      userId,
      plannedStationId,
      plannedStartTime,
      totalActualHours: actualHours.length
    });
    
    // Finde genau den passenden Eintrag
    const matches = actualHours.filter(ah => {
      const userMatch = ah.user_id === userId;
      const stationMatch = ah.station_id === plannedStationId || ah.station_id == plannedStationId;
      const timeMatch = ah.planned_start_time === plannedStartTime;
      
      console.log('🔍 Checking actual hour entry:', {
        ah_user_id: ah.user_id,
        ah_station_id: ah.station_id,
        ah_planned_start: ah.planned_start_time,
        userMatch,
        stationMatch,
        timeMatch,
        overallMatch: userMatch && stationMatch && timeMatch
      });
      
      return userMatch && stationMatch && timeMatch;
    });
    
    console.log('🔍 Found matches:', matches.length);
    return matches;
  };

  // Neue Funktion: Datumsspezifische Suche für Mitarbeiter-Jahresansicht
  const getActualHoursForShiftWithDate = (plannedShift, specificDate, filteredActualHours) => {
    const userId = plannedShift.assignedUsers?.[0]?.id || plannedShift.user_id;
    const plannedStationId = plannedShift.station_id;
    const plannedStartTime = plannedShift.start_time;
    
    // Finde genau den passenden Eintrag FÜR DAS SPEZIFISCHE DATUM
    const matches = filteredActualHours.filter(ah => {
      const userMatch = ah.user_id === userId;
      const stationMatch = ah.station_id === plannedStationId || ah.station_id == plannedStationId;
      const timeMatch = ah.planned_start_time === plannedStartTime;
      const dateMatch = ah.date === specificDate; // WICHTIG: Datum muss passen
      
      return userMatch && stationMatch && timeMatch && dateMatch;
    });
    
    return matches;
  };

  const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    
    // Parse times
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    
    // Convert to minutes
    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;
    
    // If end time is earlier than start time, it's next day (night shift)
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }
    
    const diffMinutes = endMinutes - startMinutes;
    return (diffMinutes / 60).toFixed(2);
  };

  const groupByStation = (shifts) => {
    return shifts.reduce((acc, shift) => {
      const stationId = shift.station_id;
      const stationKey = `${shift.station_id}`;
      if (!acc[stationKey]) {
        acc[stationKey] = [];
      }
      // Ensure station_id is set for rendering
      shift.station_id = stationId;
      acc[stationKey].push(shift);
      return acc;
    }, {});
  };

  // Neue Funktion: Kombiniere geplante Schichten mit manuellen Ist-Stunden-Einträgen
  const createCombinedStationView = () => {
    const grouped = groupByStation(plannedShifts);
    
    // Füge manuelle Einträge (ohne geplante Schicht) hinzu
    actualHours.forEach(actualEntry => {
      const hasPlannedShift = plannedShifts.some(shift => {
        const userId = shift.assignedUsers?.[0]?.id || shift.user_id;
        return userId === actualEntry.user_id && 
               shift.station_id == actualEntry.station_id &&
               shift.start_time === actualEntry.planned_start_time;
      });
      
      if (!hasPlannedShift) {
        // Dies ist ein manueller Eintrag - erstelle eine "virtuelle" Schicht dafür
        const stationKey = `${actualEntry.station_id}`;
        if (!grouped[stationKey]) {
          grouped[stationKey] = [];
        }
        
        // Prüfe, ob wir bereits eine virtuelle Schicht für diesen manuellen Eintrag haben
        const existsAlready = grouped[stationKey].some(shift => 
          shift.isManualEntry && 
          shift.manualEntryId === actualEntry.id
        );
        
        if (!existsAlready) {
          grouped[stationKey].push({
            // Virtuelle Schicht für manuellen Eintrag
            station_id: actualEntry.station_id,
            start_time: actualEntry.planned_start_time || actualEntry.actual_start_time,
            end_time: actualEntry.planned_end_time || actualEntry.actual_end_time,
            user_id: actualEntry.user_id,
            user_name: actualEntry.user_name,
            firstname: actualEntry.user_name?.split(' ')[0] || '',
            lastname: actualEntry.user_name?.split(' ')[1] || '',
            assignedUsers: [{
              id: actualEntry.user_id,
              name: actualEntry.user_name
            }],
            isManualEntry: true, // Kennzeichnung als manueller Eintrag
            manualEntryId: actualEntry.id
          });
        }
      }
    });
    
    return grouped;
  };

  // Neue Funktion: Gruppiere nach Mitarbeitern und dann nach Datum (für Jahresansicht)
  const createEmployeeYearView = () => {
    const groupedByDate = {};
    
    // Zuerst alle geplanten Schichten nach Datum gruppieren
    plannedShifts.forEach(shift => {
      const date = shift.date;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          plannedShifts: [],
          actualEntries: []
        };
      }
      groupedByDate[date].plannedShifts.push(shift);
    });
    
    // Dann alle Ist-Stunden nach Datum gruppieren
    actualHours.forEach(actualEntry => {
      const date = actualEntry.date;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          plannedShifts: [],
          actualEntries: []
        };
      }
      groupedByDate[date].actualEntries.push(actualEntry);
    });
    
    // Nach Datum sortieren (neueste zuerst)
    const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));
    
    return sortedDates.map(date => ({
      date,
      ...groupedByDate[date]
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Lädt...</span>
        </div>
      </div>
    );
  }

  const groupedShifts = viewMode === 'station' ? createCombinedStationView() : null;
  const employeeYearData = viewMode === 'employee' ? createEmployeeYearView() : null;

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <h2 className="mb-4">Tatsächlich geleistete Stunden</h2>
          
          <div className="card mb-4">
            <div className="card-header">
              <div className="row align-items-center">
                {viewMode === 'station' ? (
                  <div className="col-md-6">
                    <label htmlFor="dateSelect" className="form-label mb-0">
                      <strong>Datum auswählen:</strong>
                    </label>
                    <input
                      type="date"
                      id="dateSelect"
                      className="form-control mt-2"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      <button className="btn btn-sm btn-outline-secondary" title="Leeres, scan-optimiertes Formular drucken"
                        onClick={() => window.open(`${API_BASE_URL}/stundenliste/blank?token=${localStorage.getItem('token')}`, '_blank')}>
                        🖨️ Blanko-Stundenliste
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="col-md-4">
                      <label htmlFor="yearSelect" className="form-label mb-0">
                        <strong>Jahr auswählen:</strong>
                      </label>
                      <select
                        id="yearSelect"
                        className="form-control mt-2"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                      >
                        {[2024, 2025, 2026, 2027].map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="employeeSelect" className="form-label mb-0">
                        <strong>Mitarbeiter auswählen:</strong>
                      </label>
                      <select
                        id="employeeSelect"
                        className="form-control mt-2"
                        value={selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                      >
                        <option value="">-- Mitarbeiter wählen --</option>
                        {employees.map(employee => (
                          <option key={employee.id} value={employee.id}>
                            {employee.firstname} {employee.lastname}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                <div className="col-md-4 text-end">
                  <button
                    className="btn btn-outline-secondary me-2"
                    onClick={handleExportCSV}
                    title="Ist-Stunden als CSV exportieren"
                  >
                    <i className="bi bi-download me-1"></i>
                    CSV-Export
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => setShowAddEntry(true)}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Neuen Eintrag hinzufügen
                  </button>
                </div>
              </div>
            </div>
          </div>

          {rowImage && rowImage.hover && (() => {
            // Vorschau beim Überfahren: über oder unter dem Knopf, je nachdem wo mehr Platz ist; nie aus dem Bild ragen
            const w = window.innerWidth - 24; // volle Bildschirmbreite – sonst ist die Handschrift kaum lesbar (Philipp, 23.09.)
            const below = rowImage.top < window.innerHeight / 2;
            const left = 12;
            return (
              <div style={{ position: 'fixed', zIndex: 2000, left, width: w, pointerEvents: 'none', ...(below ? { top: rowImage.bottom + 6 } : { bottom: window.innerHeight - rowImage.top + 6 }) }}
                className="bg-white rounded shadow border p-2">
                <div className="small mb-1"><strong>{rowImage.name}</strong> · {rowImage.time} · Zeile vom Stundenzettel</div>
                <img alt="Zeile vom Stundenzettel" src={`${API_BASE_URL}/timesheet-entries/${rowImage.id}/row-image?token=${localStorage.getItem('token')}`} style={{ width: '100%', display: 'block' }} />
              </div>
            );
          })()}
          {rowImage && !rowImage.hover && (
            <div onClick={() => setRowImage(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}>
              <div className="bg-white rounded p-2" style={{ maxWidth: '96vw' }}>
                <div className="small mb-1"><strong>{rowImage.name}</strong> · {rowImage.time} · Zeile vom Stundenzettel</div>
                <img alt="Zeile vom Stundenzettel" src={`${API_BASE_URL}/timesheet-entries/${rowImage.id}/row-image?token=${localStorage.getItem('token')}`} style={{ maxWidth: '94vw', maxHeight: '80vh', display: 'block' }}
                  onError={(e) => { e.target.replaceWith(Object.assign(document.createElement('div'), { className: 'text-muted small p-3', textContent: 'Für diesen Eintrag gibt es (noch) keinen Ausschnitt.' })); }} />
              </div>
            </div>
          )}

          {/* Stundenzettel-Prüfung: volle Breite – links die Zeilen, rechts das Originalfoto */}
          {viewMode === 'station' && <TimesheetReview date={selectedDate} employees={employees} onApplied={fetchActualHours} />}

          {/* Tabs für verschiedene Ansichten */}
          <div className="card mb-4">
            <div className="card-header p-0">
              <ul className="nav nav-tabs card-header-tabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${viewMode === 'station' ? 'active' : ''}`}
                    onClick={() => setViewMode('station')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-building me-2"></i>
                    Nach Stationen sortiert
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${viewMode === 'employee' ? 'active' : ''}`}
                    onClick={() => setViewMode('employee')}
                    type="button"
                    role="tab"
                  >
                    <i className="fas fa-users me-2"></i>
                    Nach Mitarbeitern sortiert
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {viewMode === 'station' ? (
            // Stations-Ansicht (bestehend)
            Object.keys(groupedShifts).length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Keine geplanten Schichten oder manuellen Einträge für den {new Date(selectedDate).toLocaleDateString('de-DE')} gefunden.
              </div>
            ) : (
              Object.entries(groupedShifts).map(([stationKey, shifts]) => {
                const stationId = stationKey;
                return (
                  <div key={stationKey} className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-building me-2"></i>
                        {getStationName(stationId)}
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Mitarbeiter</th>
                              <th>Geplant</th>
                              <th>Geplante Stunden</th>
                              <th>Tatsächliche Arbeitszeiten</th>
                              <th>Ist-Stunden</th>
                              <th>Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {shifts.map((shift, index) => {
                              const actualEntries = getActualHoursForShift(shift);
                              const totalActualHours = actualEntries.reduce((sum, entry) => {
                                const hours = calculateSafeHours(entry.actual_start_time, entry.actual_end_time);
                                return sum + hours;
                              }, 0);
                              
                              // Frontend-Sicherheitscheck: Warnung bei verdächtigen Werten
                              const hasNegativeWarning = actualEntries.some(entry => {
                                const rawCalc = entry.actual_start_time && entry.actual_end_time ? 
                                  calculateSafeHours(entry.actual_start_time, entry.actual_end_time) : 0;
                                if (rawCalc <= 0 && entry.actual_start_time && entry.actual_end_time) {
                                  console.warn('🔍 Frontend detected potential midnight issue:', {
                                    entry, rawCalc, start: entry.actual_start_time, end: entry.actual_end_time
                                  });
                                  return true;
                                }
                                return false;
                              });

                              return (
                                <React.Fragment key={index}>
                                  <tr className={shift.isManualEntry ? 'table-warning' : ''}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <strong>
                                          {(() => {
                                            const userName = shift.assignedUsers?.[0]?.name || 
                                                           shift.user_name || 
                                                           (shift.firstname && shift.lastname ? `${shift.firstname} ${shift.lastname}` : '') ||
                                                           'Unbekannt';
                                            console.log(`🔍 ActualHoursTracking: Displaying name for shift:`, shift, 'Resolved name:', userName);
                                            return userName;
                                          })()}
                                        </strong>
                                        {shift.isManualEntry && (
                                          <span className="badge bg-warning text-dark ms-2" title="Manuell erfasst">
                                            <i className="fas fa-hand-paper me-1"></i>
                                            Manuell
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className={`badge ${shift.isManualEntry ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                        {shift.start_time} - {shift.end_time}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="badge bg-info">
                                        {calculateHours(shift.start_time, shift.end_time)}h
                                      </span>
                                    </td>
                                    <td>
                                      {actualEntries.length === 0 ? (
                                        <DirectTimeEntry
                                          shift={shift}
                                          onSave={saveActualHours}
                                          selectedDate={selectedDate}
                                          existingEntry={null}
                                        />
                                      ) : (
                                        // Wenn bereits Ist-Stunden existieren, zeige eine bearbeitbare Inline-Eingabe für den ersten Eintrag
                                        actualEntries.length > 1 ? (
                                          // Mit Pause geteilt: Teile + Pausen anzeigen (Bearbeiten je Teil über die Knöpfe rechts)
                                          <div className="small">
                                            {(() => {
                                              const segs = sortSegs(actualEntries);
                                              const t = (x) => { const [h, mm] = String(x).split(':').map(Number); return h * 60 + mm; };
                                              const pause = segs.slice(1).reduce((sum, e, i) => { let d = t(e.actual_start_time) - t(segs[i].actual_end_time); if (d < 0) d += 1440; return sum + d; }, 0);
                                              return (<><span className="badge bg-light text-dark border">{segs[0].actual_start_time}–{segs[segs.length - 1].actual_end_time}</span><span className="text-warning-emphasis ms-2">⏸ {pause} Min Pause (unbezahlt)</span></>);
                                            })()}
                                          </div>
                                        ) : (
                                        <DirectTimeEntry
                                          shift={shift}
                                          onSave={saveActualHours}
                                          selectedDate={selectedDate}
                                          existingEntry={actualEntries[0]}
                                        />
                                        )
                                      )}
                                    </td>
                                    <td>
                                      {actualEntries.length > 0 ? (
                                        <div className="d-flex align-items-center gap-2">
                                          <span className={`badge ${hasNegativeWarning ? 'bg-warning text-dark' : 'bg-primary'}`}>
                                            {totalActualHours.toFixed(2)}h
                                          </span>
                                          {hasNegativeWarning && (
                                            <i 
                                              className="fas fa-exclamation-triangle text-warning" 
                                              title="Frontend-Korrektur für Mitternachts-Schicht angewendet"
                                            ></i>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2" role="group">
                                        {/* Buttons für existierende Ist-Stunden */}
                                        {actualEntries.length > 0 && (
                                          <button className="btn btn-outline-warning btn-sm" title="Unbezahlte Pause einfügen – der Eintrag wird an der Pause geteilt" onClick={() => addBreak(actualEntries)}>+ Pause</button>
                                        )}
                                        {actualEntries.some((e) => e.break_of) && (
                                          <button className="btn btn-outline-secondary btn-sm" title="Letzte Pause wieder entfernen" onClick={() => removeBreak(actualEntries)}>Pause entfernen</button>
                                        )}
                                        {actualEntries.map((entry, entryIndex) => (
                                          <React.Fragment key={entryIndex}>
                                            {entry.timesheet_entry_id && (
                                              <button className="btn btn-outline-secondary btn-sm" title="Zeile vom Stundenzettel ansehen"
                                                onMouseEnter={(ev) => { const r = ev.currentTarget.getBoundingClientRect(); setRowImage({ id: entry.timesheet_entry_id, name: entry.user_name, time: `${entry.actual_start_time}–${entry.actual_end_time}`, hover: true, x: r.left, top: r.top, bottom: r.bottom }); }}
                                                onMouseLeave={() => setRowImage((cur) => (cur && cur.hover ? null : cur))}
                                                onClick={() => setRowImage({ id: entry.timesheet_entry_id, name: entry.user_name, time: `${entry.actual_start_time}–${entry.actual_end_time}` })}>📄</button>
                                            )}
                                            <button
                                              className="btn btn-outline-success btn-sm px-3"
                                              onClick={() => setEditingEntry(entry)}
                                              title="Bearbeiten"
                                            >
                                              <i className="fas fa-edit me-1"></i>
                                              Bearbeiten
                                            </button>
                                            <button
                                              className="btn btn-outline-danger btn-sm px-3"
                                              onClick={() => deleteActualHours(entry.id)}
                                              title="Löschen"
                                            >
                                              <i className="fas fa-trash me-1"></i>
                                              Löschen
                                            </button>
                                          </React.Fragment>
                                        ))}
                                        
                                        {/* Save-Button für neue Ist-Stunden (wenn keine existieren) */}
                                        {actualEntries.length === 0 && (
                                          <small className="text-muted">
                                            → Zeiten im Eingabefeld links ändern und mit grünem Button speichern
                                          </small>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            // Mitarbeiter-Jahresansicht (neu)
            !selectedEmployee ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Bitte wählen Sie einen Mitarbeiter aus, um die Jahresübersicht anzuzeigen.
              </div>
            ) : !employeeYearData || employeeYearData.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Keine Arbeitszeiten für {employees.find(e => e.id == selectedEmployee)?.firstname} {employees.find(e => e.id == selectedEmployee)?.lastname} im Jahr {selectedYear} gefunden.
              </div>
            ) : (
              employeeYearData.map((dayData, index) => {
                const displayDate = new Date(dayData.date).toLocaleDateString('de-DE', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
                
                // Kombiniere geplante Schichten und manuelle Einträge für diesen Tag
                const allShiftsForDay = [];
                const processedActualEntries = new Set(); // Verfolge bereits verarbeitete actual_hours
                
                // Füge geplante Schichten hinzu
                dayData.plannedShifts.forEach(shift => {
                  allShiftsForDay.push({
                    ...shift,
                    isPlanned: true
                  });
                });
                
                // Füge NUR manuelle Einträge hinzu (die keine entsprechende geplante Schicht haben)
                dayData.actualEntries.forEach(actualEntry => {
                  const hasPlannedShift = dayData.plannedShifts.some(shift => {
                    const userId = shift.assignedUsers?.[0]?.id || shift.user_id;
                    return userId === actualEntry.user_id && 
                           shift.station_id == actualEntry.station_id &&
                           shift.start_time === actualEntry.planned_start_time;
                  });
                  
                  if (!hasPlannedShift) {
                    allShiftsForDay.push({
                      station_id: actualEntry.station_id,
                      start_time: actualEntry.planned_start_time || actualEntry.actual_start_time,
                      end_time: actualEntry.planned_end_time || actualEntry.actual_end_time,
                      user_id: actualEntry.user_id,
                      user_name: actualEntry.user_name,
                      assignedUsers: [{
                        id: actualEntry.user_id,
                        name: actualEntry.user_name
                      }],
                      isManualEntry: true,
                      manualEntryId: actualEntry.id
                    });
                  }
                });

                return (
                  <div key={index} className="card mb-4">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-calendar-day me-2"></i>
                        {displayDate}
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Station</th>
                              <th>Geplant</th>
                              <th>Geplante Stunden</th>
                              <th>Tatsächliche Arbeitszeiten</th>
                              <th>Ist-Stunden</th>
                              <th>Aktionen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allShiftsForDay.map((shift, shiftIndex) => {
                              // Filtere actualHours nur für dieses spezifische Datum
                              const dayActualHours = actualHours.filter(ah => ah.date === dayData.date);
                              const actualEntries = getActualHoursForShiftWithDate(shift, dayData.date, dayActualHours);
                              const totalActualHours = actualEntries.reduce((sum, entry) => {
                                const hours = calculateSafeHours(entry.actual_start_time, entry.actual_end_time);
                                return sum + hours;
                              }, 0);

                              return (
                                <React.Fragment key={shiftIndex}>
                                  <tr className={shift.isManualEntry ? 'table-warning' : ''}>
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <strong>
                                          <i className="fas fa-building me-2"></i>
                                          {getStationName(shift.station_id)}
                                        </strong>
                                        {shift.isManualEntry && (
                                          <span className="badge bg-warning text-dark ms-2" title="Manuell erfasst">
                                            <i className="fas fa-hand-paper me-1"></i>
                                            Manuell
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className={`badge ${shift.isManualEntry ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                        {shift.start_time} - {shift.end_time}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="badge bg-info">
                                        {calculateHours(shift.start_time, shift.end_time)}h
                                      </span>
                                    </td>
                                    <td>
                                      {actualEntries.length === 0 ? (
                                        <DirectTimeEntry
                                          shift={shift}
                                          onSave={saveActualHours}
                                          selectedDate={dayData.date}
                                          existingEntry={null}
                                        />
                                      ) : (
                                        <DirectTimeEntry
                                          shift={shift}
                                          onSave={saveActualHours}
                                          selectedDate={dayData.date}
                                          existingEntry={actualEntries[0]}
                                        />
                                      )}
                                    </td>
                                    <td>
                                      {actualEntries.length > 0 ? (
                                        <span className="badge bg-primary">
                                          {totalActualHours.toFixed(2)}h
                                        </span>
                                      ) : (
                                        <span className="text-muted">-</span>
                                      )}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2" role="group">
                                        {actualEntries.map((entry, entryIndex) => (
                                          <React.Fragment key={entryIndex}>
                                            <button
                                              className="btn btn-outline-success btn-sm px-3"
                                              onClick={() => setEditingEntry(entry)}
                                              title="Bearbeiten"
                                            >
                                              <i className="fas fa-edit me-1"></i>
                                              Bearbeiten
                                            </button>
                                            <button
                                              className="btn btn-outline-danger btn-sm px-3"
                                              onClick={() => deleteActualHours(entry.id)}
                                              title="Löschen"
                                            >
                                              <i className="fas fa-trash me-1"></i>
                                              Löschen
                                            </button>
                                          </React.Fragment>
                                        ))}
                                        
                                        {actualEntries.length === 0 && (
                                          <small className="text-muted">
                                            → Zeiten eingeben und speichern
                                          </small>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>
      </div>

      {/* Edit/Add Modal */}
      {(editingEntry || showAddEntry) && (
        <ActualHoursModal
          entry={editingEntry}
          onSave={saveActualHours}
          onCancel={() => {
            setEditingEntry(null);
            setShowAddEntry(false);
          }}
          selectedDate={selectedDate}
          plannedShifts={plannedShifts}
        />
      )}
    </div>
  );
};

const ActualHoursModal = ({ entry, onSave, onCancel, selectedDate, plannedShifts }) => {
  const [formData, setFormData] = useState({
    user_id: '',
    user_name: '',
    date: selectedDate,
    station_id: '',
    station_name: '',
    planned_start_time: '',
    planned_end_time: '',
    actual_start_time: '',
    actual_end_time: '',
    notes: '',
    ...entry
  });

  const [users, setUsers] = useState([]);
  const [stations] = useState([
    { id: 1, name: 'G1' },
    { id: 5, name: 'G5' },
    { id: 8, name: 'G8' },
    { id: 10, name: 'G10' }
  ]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await apiCall('/users');
      setUsers(data);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.user_id || !formData.station_id || !formData.actual_start_time || !formData.actual_end_time) {
      notify('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    onSave(formData);
  };

  const handleUserChange = (userId) => {
    const user = users.find(u => u.id === parseInt(userId));
    setFormData({
      ...formData,
      user_id: parseInt(userId),
      user_name: user ? `${user.firstname} ${user.lastname}` : ''
    });
  };

  const handleStationChange = (stationId) => {
    const station = stations.find(s => s.id === parseInt(stationId));
    setFormData({
      ...formData,
      station_id: parseInt(stationId),
      station_name: normalizeStationName(null, stationId), // Sichere Normalisierung
    });
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {entry ? 'Arbeitszeit bearbeiten' : 'Neue Arbeitszeit erfassen'}
            </h5>
            <button type="button" className="btn-close" onClick={onCancel}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Mitarbeiter *</label>
                    <select
                      className="form-select"
                      value={formData.user_id}
                      onChange={(e) => handleUserChange(e.target.value)}
                      required
                    >
                      <option value="">Mitarbeiter auswählen</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.firstname} {user.lastname}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Station *</label>
                    <select
                      className="form-select"
                      value={formData.station_id}
                      onChange={(e) => handleStationChange(e.target.value)}
                      required
                    >
                      <option value="">Station auswählen</option>
                      {stations.map(station => (
                        <option key={station.id} value={station.id}>
                          {station.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Geplant von</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.planned_start_time}
                      onChange={(e) => setFormData({...formData, planned_start_time: e.target.value})}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Geplant bis</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.planned_end_time}
                      onChange={(e) => setFormData({...formData, planned_end_time: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Tatsächlich von *</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.actual_start_time}
                      onChange={(e) => setFormData({...formData, actual_start_time: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Tatsächlich bis *</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.actual_end_time}
                      onChange={(e) => setFormData({...formData, actual_end_time: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Notizen</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="z.B. Stationswechsel, Pause, besondere Ereignisse..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Abbrechen
              </button>
              <button type="submit" className="btn btn-primary">
                Speichern
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const DirectTimeEntry = ({ shift, onSave, selectedDate, existingEntry = null }) => {
  // Wenn es bereits einen Eintrag gibt, verwende dessen Zeiten als Initial-Werte
  const [startTime, setStartTime] = useState(existingEntry?.actual_start_time || shift.start_time || '');
  const [endTime, setEndTime] = useState(existingEntry?.actual_end_time || shift.end_time || '');
  
  // Initial-Werte für Vergleich speichern
  const initialStartTime = existingEntry?.actual_start_time || shift.start_time || '';
  const initialEndTime = existingEntry?.actual_end_time || shift.end_time || '';
  
  // Update local state when existingEntry changes (after successful save)
  useEffect(() => {
    if (existingEntry) {
      setStartTime(existingEntry.actual_start_time || shift.start_time || '');
      setEndTime(existingEntry.actual_end_time || shift.end_time || '');
    }
  }, [existingEntry, shift.start_time, shift.end_time]);
  
  // Buttons anzeigen wenn: noch kein Eintrag existiert ODER Zeiten geändert wurden
  const shouldShowButtons = !existingEntry || startTime !== initialStartTime || endTime !== initialEndTime;

  const handleSave = async () => {
    if (!startTime || !endTime) {
      notify('Bitte Start- und Endzeit eingeben');
      return;
    }

    // Frontend-Validierung: Überprüfe Zeitberechnung vor dem Speichern
    const calculatedHours = calculateSafeHours(startTime, endTime);
    if (calculatedHours <= 0) {
      notify('⚠️ Fehler: Die berechneten Stunden sind ungültig. Bitte überprüfen Sie die Zeiten.');
      console.error('Frontend validation failed:', { startTime, endTime, calculatedHours });
      return;
    }
    
    if (calculatedHours > 24) {
      if (!await confirmDialog(`Die berechnete Arbeitszeit beträgt ${calculatedHours}h. Das ist ungewöhnlich lang. Fortfahren?`)) {
        return;
      }
    }

    // Convert station name to station_id for backend compatibility
    const getStationId = (stationName) => {
      // Behalte station_id als String wie im Backend erwartet
      if (typeof stationName === 'string') {
        return stationName; // Falls bereits "G1", "G5", etc.
      }
      const stationMap = {
        'G1': 'G1',
        'G5': 'G5', 
        'G8': 'G8',
        'G10': 'G10'
      };
      return stationMap[stationName] || 'G1';
    };

    const entry = {
      ...(existingEntry && { id: existingEntry.id }), // ID hinzufügen wenn Update
      user_id: shift.assignedUsers?.[0]?.id || shift.user_id,
      user_name: shift.assignedUsers?.[0]?.name || 
                 shift.user_name || 
                 (shift.firstname && shift.lastname ? `${shift.firstname} ${shift.lastname}` : '') ||
                 'Unbekannt',
      date: selectedDate,
      station_id: shift.station_id || getStationId(shift.station),  // FIXED: station_id verwenden, fallback zu station
      station_name: shift.station || `Station ${shift.station_id}`,
      planned_start_time: shift.start_time,
      planned_end_time: shift.end_time,
      actual_start_time: startTime,
      actual_end_time: endTime,
      notes: existingEntry?.notes || ''
    };

    console.log(`💾 ${existingEntry ? 'Updating' : 'Creating'} actual hours entry:`, entry);
    
    try {
      await onSave(entry);
      // Nach erfolgreichem Speichern State-Werte auf die gespeicherten Werte setzen
      // Diese werden vom useEffect auch automatisch aktualisiert, aber explizit für bessere UX
      console.log('✅ Successfully saved entry, updating local state');
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      notify('Fehler beim Speichern: ' + error.message);
    }
  };

  const calculateHours = (start, end) => {
    return calculateSafeHours(start, end);
  };

  return (
    <div className="d-flex gap-2 align-items-center">
      <div className="input-group input-group-sm" style={{ width: '250px' }}>
        <input
          type="time"
          className="form-control"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          placeholder="Von"
        />
        <span className="input-group-text">-</span>
        <input
          type="time"
          className="form-control"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          placeholder="Bis"
        />
      </div>
      
      {shouldShowButtons && (
        <div className="btn-group">
          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={!startTime || !endTime}
            title="Ist-Stunden speichern"
          >
            <i className="fas fa-save me-1"></i>
            Speichern
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              // Reset zu ursprünglichen Werten (existingEntry falls vorhanden, sonst geplante Zeiten)
              const resetStartTime = existingEntry?.actual_start_time || shift.start_time || '';
              const resetEndTime = existingEntry?.actual_end_time || shift.end_time || '';
              setStartTime(resetStartTime);
              setEndTime(resetEndTime);
            }}
            title="Änderungen zurücksetzen"
          >
            <i className="fas fa-undo"></i>
          </button>
        </div>
      )}
      
      {startTime && endTime && (
        <small className="text-muted">
          ({calculateHours(startTime, endTime)}h)
        </small>
      )}
    </div>
  );
};

export default ActualHoursTracking;
